import {
  buildPersonCvUrl,
  buildPersonTailoredCvUrl,
} from '@tabbio/utils/public-url';

const DEFAULT_PUBLIC_SITE_URL = 'https://www.tabbio.com';

function isFalseLike(value?: string | null) {
  if (!value) {
    return false;
  }

  return ['0', 'false', 'no', 'off'].includes(value.trim().toLowerCase());
}

function isTrueLike(value?: string | null) {
  if (!value) {
    return false;
  }

  return ['1', 'true', 'yes', 'on'].includes(value.trim().toLowerCase());
}

function getNormalizedSiteUrl() {
  const configuredEnv = process.env.EXPO_PUBLIC_SITE_URL;
  const configured = configuredEnv ? configuredEnv.trim() : '';
  if (!configured) {
    return DEFAULT_PUBLIC_SITE_URL;
  }

  if (configured.startsWith('http://') || configured.startsWith('https://')) {
    return configured.replace(/\/+$/, '');
  }

  return `https://${configured.replace(/\/+$/, '')}`;
}

export function isPublicWebPublishingEnabled() {
  const disabledEnv = process.env.EXPO_PUBLIC_DISABLE_PUBLIC_WEB_PUBLISHING;
  const enabledEnv = process.env.EXPO_PUBLIC_PUBLIC_WEB_ENABLED;

  if (isTrueLike(disabledEnv)) return false;
  if (isFalseLike(enabledEnv)) return false;
  return true;
}

export function getPublicSiteUrl() {
  if (!isPublicWebPublishingEnabled()) {
    return null;
  }

  return getNormalizedSiteUrl();
}

export function getPublicSiteLabel() {
  const siteUrl = getPublicSiteUrl();
  if (!siteUrl) {
    return null;
  }

  // Strip `www.` so callers that compose `{handle}.{label}` render
  // `jordan.tabbio.com`, not `jordan.www.tabbio.com` (issue #241). The
  // configured EXPO_PUBLIC_SITE_URL is `https://www.tabbio.com`, but the
  // registrable domain that carries profile subdomains is `tabbio.com`.
  // Mirrors getPublicRootDomain() in @tabbio/utils/public-url, which strips
  // `www.` for exactly this reason: the two builders must agree.
  return siteUrl.replace(/^https?:\/\//, '').replace(/^www\./, '');
}

export function buildPublicProfileUrl(
  handle: string,
  mode: 'individual' | 'company',
) {
  const siteUrl = getPublicSiteUrl();
  if (!siteUrl || !handle.trim()) {
    return null;
  }

  // Companies and individuals share one handle namespace and one canonical
  // root path: apps/web serves company pages at /{slug} (the /c/{slug} route
  // is only a permanent redirect onto it), and the server-built publicUrl,
  // sitemaps, and JSON-LD all use the root form. Share/QR must emit the
  // canonical URL (PRD §13), so both modes resolve identically; `mode` stays
  // in the signature for call-site clarity.
  void mode;
  return new URL(`/${handle.trim()}`, siteUrl).toString();
}

export function buildPublicMessageUrl(username: string) {
  const siteUrl = getPublicSiteUrl();
  if (!siteUrl || !username.trim()) {
    return null;
  }

  return new URL(`/message/${username.trim()}`, siteUrl).toString();
}

export function buildPublicCvRouteUrl(handle: string) {
  if (!handle.trim()) {
    return null;
  }

  // The canonical WebCV is a permanent product surface. Share dialogs must
  // keep showing it even when a stale public-publishing kill switch is present
  // in an app build; hiding the URL also removes the contact/private-link block.
  // Other optional public-web surfaces still honor getPublicSiteUrl().
  return buildPersonCvUrl(handle, getNormalizedSiteUrl());
}

/**
 * The Tabbio CV link as printed inside the CV document itself.
 *
 * The label is always the canonical public host (`{handle}.tabbio.com`,
 * via the shared builder's production domain): a CV is a document, and the
 * address it prints must be the one a reader would type — never a localhost
 * dev origin or a `/handle/cv` path. The href stays environment-driven so
 * tapping the row in a dev build still opens the local public web.
 */
export function buildPublicCvDisplayLink(handle: string) {
  const clean = handle.trim();
  if (!clean) {
    return null;
  }

  const href = buildPublicCvRouteUrl(clean);
  if (!href) {
    return null;
  }

  const canonical = buildPersonCvUrl(clean);
  return {
    href,
    label: canonical.replace(/^https?:\/\//, '').replace(/\/$/, ''),
  };
}

export function buildPublicTailoredCvRouteUrl(handle: string, slug: string) {
  const siteUrl = getPublicSiteUrl();
  if (!siteUrl || !handle.trim() || !slug.trim()) {
    return null;
  }

  return buildPersonTailoredCvUrl(handle, slug, siteUrl);
}

export function buildPublicCvSubdomainUrl(username: string) {
  const siteUrl = getPublicSiteUrl();
  if (!siteUrl || !username.trim()) {
    return null;
  }

  return buildPersonCvUrl(username, siteUrl);
}

/**
 * Canonical public share page for a catalog skill: skills.<domain>/<skillId>.
 * This is the viral link every Share and Copy button hands out; the page
 * carries the apply funnel for visitors without an account.
 *
 * Deliberately not gated by getPublicSiteUrl()/getPublicSiteLabel(), which
 * null out under EXPO_PUBLIC_DISABLE_PUBLIC_WEB_PUBLISHING: that switch
 * governs publishing user content, not the public marketing skill catalog
 * (same reasoning as buildPublicCvRouteUrl above). A non-empty skillId is a
 * caller precondition, so the result is always a usable URL.
 *
 * Production output must stay byte-identical to buildPublicSkillShareUrl in
 * apps/web/src/lib/public-skills.ts: a link shared from the app and the
 * canonical tag on the page it opens have to agree, so the two builders must
 * agree too.
 */
export function buildPublicSkillShareUrl(skillId: string): string {
  const slug = encodeURIComponent(skillId.trim());
  const origin = /^(https?:\/\/)([^/?#]+)/.exec(getNormalizedSiteUrl());
  if (!origin) {
    return `https://skills.tabbio.com/${slug}`;
  }

  const [, protocol, host] = origin;
  return `${protocol}skills.${host.toLowerCase().replace(/^www\./, '')}/${slug}`;
}

export function buildPublicShortlistUrl(pathname: string) {
  const siteUrl = getPublicSiteUrl();
  if (!siteUrl || !pathname.trim()) {
    return null;
  }

  return new URL(pathname, siteUrl).toString();
}

/**
 * URL of the apps/web live-preview route the editor iframe should point at.
 * Falls back to the public site URL when no dedicated preview origin is set.
 */
export function buildPublicProfilePreviewUrl(handle: string) {
  const clean = handle.trim();
  if (!clean) return null;

  const overrideEnv =
    process.env.EXPO_PUBLIC_PREVIEW_SITE_URL ?? process.env.EXPO_PUBLIC_SITE_URL;
  const override = overrideEnv ? overrideEnv.trim() : '';
  const base =
    override.length > 0
      ? override.startsWith('http://') || override.startsWith('https://')
        ? override.replace(/\/+$/, '')
        : `https://${override.replace(/\/+$/, '')}`
      : getPublicSiteUrl();
  if (!base) return null;

  return new URL(`/public-profile/${encodeURIComponent(clean)}/preview`, base).toString();
}
